import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Receipt } from '../../types/expense';
import { FileText, Send, Bot, User, Check, X, Sparkles } from 'lucide-react';
import { base64ToDataUrl } from '../../lib/utils';
import { ScrollArea } from '../ui/scroll-area';
import { getReceiptTypeLabel, getReceiptTypeConfig } from '../../config/receipt-types';
import { Badge } from '../ui/badge';
import { receiptTypeService, ReceiptTypeDto } from '../../services/receiptTypeService';
import { useDataMode } from '@/providers/DataModeProvider';

interface ReviewReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receipt: Receipt | null;
  onConfirm: (updatedReceipt: Partial<Receipt>) => void;
  onDiscard: () => void;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export function ReviewReceiptDialog({
  open,
  onOpenChange,
  receipt,
  onConfirm,
  onDiscard,
}: ReviewReceiptDialogProps) {
  const { isLiveMode } = useDataMode();
  const [formData, setFormData] = useState({
    supplier: '',
    amount: '',
    transactionDate: '',
    description: '',
    receiptType: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [receiptTypes, setReceiptTypes] = useState<ReceiptTypeDto[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper function to parse natural language dates
  const parseNaturalDate = (input: string): string | null => {
    if (!input || typeof input !== 'string') return null;
    const lowerInput = input.toLowerCase();
    const today = new Date();
    
    if (lowerInput.includes('today')) {
      return today.toISOString().split('T')[0];
    }
    
    if (lowerInput.includes('yesterday')) {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString().split('T')[0];
    }
    
    if (lowerInput.includes('tomorrow')) {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    }
    
    // Check for "X days ago"
    const daysAgoMatch = lowerInput.match(/(\d+)\s+days?\s+ago/);
    if (daysAgoMatch) {
      const daysAgo = parseInt(daysAgoMatch[1]);
      const date = new Date(today);
      date.setDate(date.getDate() - daysAgo);
      return date.toISOString().split('T')[0];
    }
    
    // Check for "last week"
    if (lowerInput.includes('last week')) {
      const date = new Date(today);
      date.setDate(date.getDate() - 7);
      return date.toISOString().split('T')[0];
    }
    
    return null;
  };

  // Helper function to get receipt type label for display
  const getReceiptTypeDisplayLabel = (typeId: string): string => {
    const type = receiptTypes.find(t => t.id === typeId);
    if (type) {
      return type.name === 'OfficeSupplies' ? 'Office Supplies' : type.name;
    }
    const config = getReceiptTypeConfig(typeId);
    return config ? (config.name === 'OfficeSupplies' ? 'Office Supplies' : config.name) : 'Other';
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch receipt types when dialog opens
  useEffect(() => {
    if (open) {
      receiptTypeService.getAll(isLiveMode).then(setReceiptTypes).catch(console.error);
    }
  }, [open, isLiveMode]);

  // Helper function to detect receipt type based on merchant name
  const detectReceiptType = (supplier: string): string | undefined => {
    if (!supplier || typeof supplier !== 'string') return undefined;
    const lowerSupplier = supplier.toLowerCase();
    
    // Supermarkets -> Shopping
    const supermarkets = ['tesco', 'asda', 'morrisons', 'sainsbury', 'sainsburys', 'lidl', 'aldi', 'waitrose', 'co-op', 'coop'];
    if (supermarkets.some(market => lowerSupplier.includes(market))) {
      const shoppingType = receiptTypes.find(t => t.name === 'Shopping');
      return shoppingType?.id;
    }
    
    // Restaurants/Food -> Meals
    const restaurants = ['restaurant', 'cafe', 'coffee', 'pizza', 'burger', 'mcdonald', 'kfc', 'subway', 'starbucks', 'costa'];
    if (restaurants.some(place => lowerSupplier.includes(place))) {
      const mealsType = receiptTypes.find(t => t.name === 'Meals');
      return mealsType?.id;
    }
    
    // Fuel stations -> Fuel
    const fuelStations = ['shell', 'bp', 'esso', 'texaco', 'petrol', 'fuel'];
    if (fuelStations.some(station => lowerSupplier.includes(station))) {
      const fuelType = receiptTypes.find(t => t.name === 'Fuel');
      return fuelType?.id;
    }
    
    // Hotels -> Accommodation
    const hotels = ['hotel', 'inn', 'lodge', 'resort', 'airbnb'];
    if (hotels.some(place => lowerSupplier.includes(place))) {
      const accommodationType = receiptTypes.find(t => t.name === 'Accommodation');
      return accommodationType?.id;
    }
    
    return undefined;
  };

  useEffect(() => {
    if (receipt && open) {
      // Use the receipt type from the receipt if it exists, otherwise detect it
      let receiptTypeId = receipt.receiptType || undefined;
      
      // If no receipt type, try to detect it based on merchant name
      if (!receiptTypeId && receiptTypes.length > 0) {
        const supplier = receipt.supplier || '';
        receiptTypeId = detectReceiptType(supplier);
        
        // If still no match, default to "Other" instead of first in list
        if (!receiptTypeId) {
          const otherType = receiptTypes.find(t => t.name === 'Other');
          receiptTypeId = otherType?.id || receiptTypes[0]?.id;
        }
      }
      
      setFormData({
        supplier: receipt.supplier || '',
        amount: receipt.amount?.toString() || '',
        transactionDate: receipt.transactionDate
          ? new Date(receipt.transactionDate).toISOString().split('T')[0]
          : '',
        description: receipt.description || '',
        receiptType: receiptTypeId || '',
      });

      // Get receipt type from the actual API data, not the hardcoded config
      const receiptTypeObj = receiptTypes.find(t => t.id === receiptTypeId);
      const receiptTypeLabel = receiptTypeObj ? (receiptTypeObj.name === 'OfficeSupplies' ? 'Office Supplies' : receiptTypeObj.name) : 'Other';

      // Initialize chat with greeting
      setMessages([
        {
          id: '1',
          role: 'assistant',
          content: `Hi! I've scanned your receipt and extracted the following information:\n\n🏷️ **Receipt Type:** ${receiptTypeLabel}\n📍 **Merchant:** ${receipt.supplier || 'Not detected'}\n💰 **Amount:** £${receipt.amount?.toFixed(2) || '0.00'}\n📅 **Date:** ${receipt.transactionDate ? new Date(receipt.transactionDate).toLocaleDateString() : 'Not detected'}\n📝 **Description:** ${receipt.description || 'Not detected'}\n\nIs this information correct? You can ask me to change any field (including the receipt type), or type "confirm" to save the receipt.`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [receipt, open, receiptTypes]);

  const simulateAIResponse = (userMessage: string): string => {
    if (!userMessage || typeof userMessage !== 'string') {
      return "I didn't receive a valid message. Please try again.";
    }
    
    const lowerMessage = userMessage.toLowerCase();

    // Handle confirmation
    if (lowerMessage.includes('confirm') || lowerMessage.includes('correct') || lowerMessage.includes('yes') || lowerMessage === 'ok') {
      return "Great! I'll save this receipt with the current information. Click the 'Confirm & Save' button below to complete the process.";
    }

    // Handle discard
    if (lowerMessage.includes('discard') || lowerMessage.includes('delete') || lowerMessage.includes('cancel')) {
      return "I understand you want to discard this receipt. Click the 'Discard Receipt' button below to remove it.";
    }

    // Handle receipt type changes
    if (lowerMessage.includes('receipt type') || lowerMessage.includes('type') || lowerMessage.includes('category')) {
      // Check for specific receipt types from the fetched list
      for (const type of receiptTypes) {
        if (!type || !type.name || typeof type.name !== 'string') continue;
        
        const typeName = type.name.toLowerCase();
        const typeLabel = type.name === 'OfficeSupplies' ? 'office supplies' : typeName;
        // Also check for space-separated versions (e.g., "office supplies")
        const spacedName = type.name.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
        
        if (lowerMessage.includes(typeName) || lowerMessage.includes(typeLabel) || lowerMessage.includes(spacedName)) {
          setFormData(prev => ({ ...prev, receiptType: type.id }));
          const displayLabel = type.name === 'OfficeSupplies' ? 'Office Supplies' : type.name;
          return `Updated receipt type to: **${displayLabel}**\n\nAnything else you'd like to change?`;
        }
      }
      
      // If no specific type found, show available options
      const typesList = receiptTypes.map(t => {
        const name = t.name === 'OfficeSupplies' ? 'Office Supplies' : t.name;
        // Add spaces before capital letters for better readability
        return name.replace(/([A-Z])/g, ' $1').trim();
      }).join(', ');
      return `What receipt type would you like to set? Available types:\n\n${typesList}\n\nFor example: "Change type to Receipt" or "Set receipt type to Invoice"`;
    }

    // Handle merchant/supplier changes
    if (lowerMessage.includes('merchant') || lowerMessage.includes('supplier') || lowerMessage.includes('store') || lowerMessage.includes('shop')) {
      const match = userMessage.match(/(?:merchant|supplier|store|shop)(?:\s+is|\s+to|\s+should be)?\s+(.+)/i);
      if (match) {
        const newSupplier = match[1].trim().replace(/['"]/g, '');
        setFormData(prev => ({ ...prev, supplier: newSupplier }));
        return `✅ Updated merchant to: **${newSupplier}**\n\nAnything else you'd like to change?`;
      }
      return `What would you like to change the merchant to? For example: "Change merchant to Tesco"`;
    }

    // Handle amount changes
    if (lowerMessage.includes('amount') || lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('£')) {
      const match = userMessage.match(/(?:amount|price|cost)(?:\s+is|\s+to|\s+should be)?\s+£?(\d+\.?\d*)/i) || 
                    userMessage.match(/£(\d+\.?\d*)/);
      if (match) {
        const newAmount = match[1];
        setFormData(prev => ({ ...prev, amount: newAmount }));
        return `✅ Updated amount to: **£${parseFloat(newAmount).toFixed(2)}**\n\nAnything else you'd like to change?`;
      }
      return `What would you like to change the amount to? For example: "Change amount to £25.50"`;
    }

    // Handle date changes
    if (lowerMessage.includes('date') || lowerMessage.includes('when') || lowerMessage.includes('day') || lowerMessage.includes('today') || lowerMessage.includes('yesterday')) {
      // Try natural language date parsing first
      const naturalDate = parseNaturalDate(userMessage);
      if (naturalDate) {
        setFormData(prev => ({ ...prev, transactionDate: naturalDate }));
        return `✅ Updated date to: **${new Date(naturalDate).toLocaleDateString()}**\n\nAnything else you'd like to change?`;
      }
      
      // Try standard date format
      const match = userMessage.match(/(?:date)(?:\s+is|\s+to|\s+should be)?\s+(\d{4}-\d{2}-\d{2})/i);
      if (match) {
        const newDate = match[1];
        setFormData(prev => ({ ...prev, transactionDate: newDate }));
        return `✅ Updated date to: **${new Date(newDate).toLocaleDateString()}**\n\nAnything else you'd like to change?`;
      }
      return `What would you like to change the date to? You can use:\n\n• Natural language: "today", "yesterday", "3 days ago", "last week"\n• Standard format: YYYY-MM-DD (e.g., "2024-01-15")\n\nFor example: "Change date to yesterday" or "Set date to 2024-01-15"`;
    }

    // Handle description changes
    if (lowerMessage.includes('description') || lowerMessage.includes('note') || lowerMessage.includes('details')) {
      const match = userMessage.match(/(?:description|note|details)(?:\s+is|\s+to|\s+should be)?\s+(.+)/i);
      if (match) {
        const newDescription = match[1].trim().replace(/['"]/g, '');
        setFormData(prev => ({ ...prev, description: newDescription }));
        return `✅ Updated description to: **${newDescription}**\n\nAnything else you'd like to change?`;
      }
      return `What would you like to change the description to? For example: "Change description to Business lunch with client"`;
    }

    // Handle show/summary requests
    if (lowerMessage.includes('show') || lowerMessage.includes('summary') || lowerMessage.includes('what') || lowerMessage.includes('current')) {
      const receiptTypeConfig = getReceiptTypeConfig(formData.receiptType);
      const receiptTypeLabel = receiptTypeConfig ? (receiptTypeConfig.name === 'OfficeSupplies' ? 'Office Supplies' : receiptTypeConfig.name) : 'Other';
      return `Here's the current receipt information:\n\n🏷️ **Receipt Type:** ${receiptTypeLabel}\n📍 **Merchant:** ${formData.supplier || 'Not set'}\n💰 **Amount:** £${formData.amount ? parseFloat(formData.amount).toFixed(2) : '0.00'}\n📅 **Date:** ${formData.transactionDate ? new Date(formData.transactionDate).toLocaleDateString() : 'Not set'}\n📝 **Description:** ${formData.description || 'Not set'}\n\nWould you like to change anything?`;
    }

    // Handle help requests
    if (lowerMessage.includes('help') || lowerMessage === '?') {
      return `I can help you review and edit your receipt! Here's what you can do:\n\n• Say "change type to [type]" to update the receipt type\n• Say "change merchant to [name]" to update the merchant\n• Say "change amount to £[amount]" to update the amount\n• Say "change date to today/yesterday" or use YYYY-MM-DD format\n• Say "change description to [text]" to update the description\n• Say "show summary" to see current information\n• Say "confirm" when everything looks good\n• Say "discard" to delete this receipt\n\nWhat would you like to do?`;
    }

    // Default response
    return `I'm not sure I understood that. You can:\n\n• Ask me to change the **receipt type**, **merchant**, **amount**, **date**, or **description**\n• Type "**show summary**" to see current information\n• Type "**confirm**" to save the receipt\n• Type "**help**" for more options\n\nWhat would you like to do?`;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate AI thinking time
    setTimeout(() => {
      const aiResponse = simulateAIResponse(inputMessage);
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 800);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleConfirm = () => {
    onConfirm({
      supplier: formData.supplier,
      amount: parseFloat(formData.amount),
      transactionDate: formData.transactionDate,
      description: formData.description,
      receiptType: formData.receiptType,
    });
  };

  const handleDiscard = () => {
    onDiscard();
    // Don't call onOpenChange here - parent's onDiscard handles closing
  };

  if (!receipt) return null;

  // Safely get image data with fallback
  const getImageSrc = () => {
    try {
      const imageData = receipt.annotatedImageData || receipt.imageData;
      if (!imageData) {
        console.error('No image data available for receipt');
        return '';
      }
      return base64ToDataUrl(imageData);
    } catch (error) {
      console.error('Error processing image data:', error);
      return '';
    }
  };

  const imageSrc = getImageSrc();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] md:h-[85vh] p-0 flex flex-col">
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b flex-shrink-0">
          <DialogTitle className="text-lg sm:text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            Review Receipt with AI Assistant
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Chat with our AI to review and edit the extracted receipt information
          </DialogDescription>
        </DialogHeader>

        {/* Duplicate Warning Box */}
        {(receipt as any).potentialDuplicates && (receipt as any).potentialDuplicates.length > 0 && (
          <div className="mx-3 sm:mx-6 mt-2 sm:mt-4 p-3 sm:p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 text-sm sm:text-base mb-1">
                  <span className="block sm:inline">⚠️ Duplicate Alert</span>
                </h3>
                <p className="text-yellow-700 dark:text-yellow-300 text-xs sm:text-sm">
                  Found {(receipt as any).potentialDuplicates.length} similar receipt{(receipt as any).potentialDuplicates.length > 1 ? 's' : ''} in the system. Please verify before saving!
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 flex-1 overflow-hidden">
          {/* Receipt Image Preview - Hidden on mobile, shown on desktop */}
          <div className="hidden md:flex p-6 border-r bg-muted/30 flex-col overflow-hidden">
            <div className="flex-1 relative overflow-hidden rounded-lg border bg-background mb-4">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt="Receipt preview"
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    console.error('Image failed to load');
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <p>Image not available</p>
                </div>
              )}
              {receipt.annotatedImageData && (
                <div className="absolute top-2 right-2 bg-primary/90 text-primary-foreground text-xs px-2 py-1 rounded-md flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  OCR Detected
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-shrink-0">
              <FileText className="h-4 w-4" />
              <span>Uploaded {new Date(receipt.uploadedAt).toLocaleString()}</span>
            </div>
          </div>

          {/* AI Chat Interface - Full width on mobile */}
          <div className="flex flex-col overflow-hidden">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-2 sm:gap-3 ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                      </div>
                    )}
                    <div
                      className={`rounded-lg px-3 py-2 sm:px-4 sm:py-3 max-w-[85%] sm:max-w-[80%] ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="text-xs sm:text-sm whitespace-pre-wrap break-words">
                        {message.content.split('\n').map((line, i) => {
                          // Handle bold text
                          const parts = line.split(/(\*\*.*?\*\*)/g);
                          return (
                            <div key={i}>
                              {parts.map((part, j) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return (
                                    <strong key={j} className="font-semibold">
                                      {part.slice(2, -2)}
                                    </strong>
                                  );
                                }
                                return <span key={j}>{part}</span>;
                              })}
                            </div>
                          );
                        })}
                      </div>
                      <div className="text-[10px] sm:text-xs opacity-70 mt-1">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                    {message.role === 'user' && (
                      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center">
                        <User className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                {isTyping && (
                  <div className="flex gap-2 sm:gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    </div>
                    <div className="rounded-lg px-3 py-2 sm:px-4 sm:py-3 bg-muted">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Chat Input */}
            <div className="p-3 sm:p-4 border-t bg-background flex-shrink-0">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  className="flex-1 text-sm"
                  disabled={isTyping}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isTyping}
                  size="icon"
                  className="h-9 w-9 sm:h-10 sm:w-10"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex gap-2 mt-2 sm:mt-3">
                <Button
                  variant="outline"
                  onClick={handleDiscard}
                  className="flex-1 gap-1 sm:gap-2 text-xs sm:text-sm h-8 sm:h-9"
                  size="sm"
                >
                  <X className="h-3 w-3 sm:h-4 sm:w-4" />
                  Discard
                </Button>
                <Button
                  onClick={handleConfirm}
                  className="flex-1 gap-1 sm:gap-2 text-xs sm:text-sm h-8 sm:h-9"
                  size="sm"
                  disabled={
                    !formData.supplier ||
                    !formData.amount ||
                    !formData.transactionDate
                  }
                >
                  <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                  Confirm & Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
